import { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';

import { TextField, Button, Dialog, Dropdown } from '@marketplace/ui-elements';
import { Icon } from '@developer/components';
import {
  useAPIFetchDeveloper,
  useAPIPutDeveloper,
  TAPIFetchDeveloper,
  useAPIPutDeveloperResetPassowrd,
  TAPIHookFetchCountryDetailsResponse,
  useAPIFetchCountryDetails
} from '@marketplace/api';

import classes from './personal-profile-form.module.scss';
import { personalProfileValidationSchema } from './personal-profile-form.validation';
import { joiResolver } from '@hookform/resolvers/joi';
import {ResetPasswordModal} from './reset-password-modal'
import { Completion } from 'src/pages/app-page/components';

/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { MARKETPLACE_DEV_CONTEXT } from '../../../../../../../libs/common/src/features';
import { UserType } from '../../../../../../../libs/common/src/features/auth/auth.types';
import { DropdownChangeParams } from 'primereact/dropdown';

type TPersonalProfileFormFields = Omit<TAPIFetchDeveloper['data'], 'id' & 'email'>;

export const PersonalProfileForm = () => {
  const { formatMessage } = useIntl();
  const [isEditMode, setIsEditMode] = useState(false);
  const { data, fetchDeveloper } = useAPIFetchDeveloper();
  const { putDeveloper, loading } = useAPIPutDeveloper();
  const { putDeveloperResetPassword} = useAPIPutDeveloperResetPassowrd();
  const [isOpen, setIsOpen] = useState(false);
  const [isSuccessModalOpen,SetIsSuccessModalOpen] = useState(false);
  const [isErrorModalOpen,SetIsErrorModalOpen] = useState(false);
  const {
    data: countryDetails,
    loading: isFetchCountryDetailsLoading,
    fetchCountryDetails
  }: TAPIHookFetchCountryDetailsResponse = useAPIFetchCountryDetails({ silence: true });
  const [isdCode, setIsd_code] = useState('');
  const [password,setPassword] = useState('');
  const [confirmPassword,setConfirmPassword] = useState('');

  useEffect(() => {
    (async () => {
      const countryDeatilsList = await fetchCountryDetails();
      const personalData = await fetchDeveloper().then((res) => res.data);
      setIsd_code(countryDeatilsList?.find((item) => item?.id === personalData?.dial_country_id)?.isd_code || '');
      reset({
        first_name: personalData?.first_name,
        last_name: personalData?.last_name,
        dial_country_id: countryDeatilsList?.find((item) => item?.id === personalData?.dial_country_id),
        isd_code: countryDeatilsList?.find((item) => item?.id === personalData?.dial_country_id)?.isd_code,
        phone_number: personalData?.phone_number,
      });
    })();
  }, []);

  const editModeOn = () => {setIsEditMode(true);};
  const editModeOff = () => {setIsEditMode(false);};
  const openModal = () => setIsOpen(true);
  const closeModal = () => {setIsOpen(false); setPassword(''); setConfirmPassword('');}
  const openSuccessModal = () => SetIsSuccessModalOpen(true)
  const closeSuccessModal = () => SetIsSuccessModalOpen(false)
  const openErrorModal = () => SetIsErrorModalOpen(true)
  const closeErrorModal = () => {SetIsErrorModalOpen(false); setPassword(''); setConfirmPassword('');}

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    formState: { errors, isValid, isDirty },
  } = useForm<TPersonalProfileFormFields>({
    mode: 'onChange',
    resolver: joiResolver(personalProfileValidationSchema({ formatMessage })),
  });

  const updatePassword =  async (password : string) => {
    await putDeveloperResetPassword({"userPassword": password}).then((res) => {
      if(res.data != null)
      {
        closeModal();
        closeErrorModal();
        openSuccessModal();
      }
      else
      {
        closeSuccessModal();
        openErrorModal();
      }
    });
  }

  const saveProfile = async (data: TPersonalProfileFormFields) => {
    const response = await putDeveloper({first_name: data?.first_name,
      last_name: data?.last_name,
      phone_number: data?.phone_number,
      dial_country_id: data?.dial_country_id?.id}).then((res) => res.data);
    setIsd_code(countryDetails?.find((item) => item?.id === response?.dial_country_id)?.isd_code || '');
    reset({
      first_name: response?.first_name,
      last_name: response?.last_name,
      dial_country_id: countryDetails?.find((item) => item?.id === response?.dial_country_id),
      isd_code: countryDetails?.find((item) => item?.id === response?.dial_country_id)?.isd_code,
      phone_number: response?.phone_number,
    });
    data.dial_country_id = countryDetails?.find((item) => item?.id === response?.dial_country_id);
    editModeOff();
  };

  const cancelEditMode = () => {
    (async () => {
      const countryDeatilsList = await fetchCountryDetails();
      const personalData = await fetchDeveloper().then((res) => res.data);
      setIsd_code(countryDeatilsList?.find((item) => item?.id === personalData?.dial_country_id)?.isd_code || '');
      reset({
        first_name: personalData?.first_name,
        last_name: personalData?.last_name,
        dial_country_id: countryDeatilsList?.find((item) => item?.id === personalData?.dial_country_id),
        isd_code: countryDeatilsList?.find((item) => item?.id === personalData?.dial_country_id)?.isd_code,
        phone_number: personalData?.phone_number,
      });
    })();
    editModeOff();
    reset();
  };

  const isPasswordResetEnabled = () => {
    const dev_context = localStorage.getItem(MARKETPLACE_DEV_CONTEXT);
    if(dev_context) {
      const user_type =  JSON.parse(dev_context)?.type;
      return (user_type != UserType.THIRD_PARTY_DEV && user_type != UserType.PATIENT && user_type != UserType.EHR_USER);
    }
    return false;
  };

  const handleCountryForDialCode = (e: DropdownChangeParams) => {
    setValue('dial_country_id', e.value, { shouldDirty: true, shouldValidate: true });
    setIsd_code(e.value.isd_code);
  }

  return (
    <form onSubmit={handleSubmit(saveProfile)} className={classes.form}>
      <button
        className={classes.edit_button}
        onClick={editModeOn}
        type="button"
      >
        <Icon
        classes={isEditMode ? classes.editIcon_active : classes.editIcon}
        variant="edit-pencil"
        isPointer
      />
      </button>
      <div className={classes.fields_section}>
        <TextField
          name="first_name"
          fieldId="first_name"
          isRequired
          disabled={!isEditMode}
          error={errors?.first_name?.message}
          control={control}
          maxLength={100}
          autoComplete="off"
          label={formatMessage({
            id: 'developer_personal_page_personal_profile_first_name',
          })}
        />
        <TextField
          name="last_name"
          fieldId="last_name"
          isRequired
          disabled={!isEditMode}
          control={control}
          error={errors?.last_name?.message}
          autoComplete="off"
          maxLength={100}
          label={formatMessage({
            id: 'developer_personal_page_personal_profile_last_name',
          })}
        />
      </div>
      <div className={classes.customRow}>
        <Dropdown
          isRequired
          filter
          filterBy="country_name"
          options={countryDetails}
          onChange={handleCountryForDialCode}
          control={control}
          error={errors?.country?.message}
          isLoading={isFetchCountryDetailsLoading}
          rootClassName={classes.countryField}
          label={formatMessage({
            id: 'developer_personal_page_personal_profile_phone',
          })}
          fieldId="dial_country_id"
          optionLabel="country_name"
          name="dial_country_id"
          labelClassName={classes.dropDownLabel}
          disabled={!isEditMode}
          id='developer_personal_page_personal_profile_country_isd'
        />
        <TextField
          disabled={true}
          type="text"
          fieldId="isd_code"
          name='isd_code'
          value={isdCode}
          rootClassName={classes.isd_wrapper}
          inputClassName={classes.isd_input}
        />
        <TextField
          name="phone_number"
          fieldId="phone_number"
          disabled={!isEditMode}
          control={control}
          autoComplete="off"
          error={errors?.phone_number?.message}
          maxLength={10}
          rootClassName={classes.phoneField}
        />
      </div>
      <div className={classes.fields_section}>
        <div className={classes.email_wrapper}>
          <div className={classes.email_label}>
            {formatMessage({
              id: 'developer_personal_page_personal_profile_email',
            })}
          </div>
          <div className={classes.email}>{data?.email}</div>
        </div>
      </div>
      <Button
        variant="secondary"
        disabled={!isEditMode}
        type="button"
        onClick={(e) => {
          e.preventDefault();
          openModal()}}
        hidden={isPasswordResetEnabled()}
      >
        {formatMessage({
          id: 'developer_personal_page_personal_change_password',
        })}
      </Button>
      <ResetPasswordModal isOpen = {isOpen} password={password} confirmPasssword={confirmPassword} setPassword={setPassword} setConfirmPassword={setConfirmPassword} onReject={closeModal} onAccept ={updatePassword}>
      </ResetPasswordModal>
      <Dialog
        visible={isSuccessModalOpen || isErrorModalOpen}
        header={formatMessage({ id: isSuccessModalOpen? 'dev_password_reset_success_title': 'dev_password_reset_error_title' })}
        acceptLabel={''}
        rejectLabel={''}
        onClick = { isSuccessModalOpen ? closeSuccessModal : closeErrorModal}
        >
          <Completion
          onCloseModalHandler={closeModal}
          complectionText = {formatMessage({ id : isSuccessModalOpen? 'dev_password_reset_success_modal_text' : 'dev_password_reset_error_modal_text'})}
          messageType= {isSuccessModalOpen?'Success':'Error'} ></Completion>
      </Dialog>
        {isEditMode && (
          <div className={classes.button_wrapper}>
            <Button variant="secondary" type="button" onClick={cancelEditMode}>
              {formatMessage({
                id: 'developer_personal_page_cancel',
              })}
            </Button>
            <Button
              type="submit"
              disabled={(isValid && !isDirty) || !isValid}
              loading={loading}
            >
              {formatMessage({
                id: 'developer_personal_page_save',
              })}
            </Button>
        </div>
        )}
    </form>
  );
};
